/**
 * Centralized Teacher Tools Configuration
 *
 * This file contains all configuration for teacher tools including:
 * - Tool metadata (title, description, routes, icons)
 * - Dynamic form field definitions
 * - API payload transformations
 * - Canvas message templates
 */

// Field type constants for dynamic form generation
export const FIELD_TYPES = {
  EDUCATION_YEAR: 'EDUCATION_YEAR',
  TEXT_OR_LESSON: 'TEXT_OR_LESSON',
  TEXTAREA: 'TEXTAREA',
  TEXTAREA_WITH_COUNT: 'TEXTAREA_WITH_COUNT',
  TEXT: 'TEXT',
  NUMBER: 'NUMBER',
  DROPDOWN: 'DROPDOWN',
  SPECIAL_NEEDS: 'SPECIAL_NEEDS',
  ADDITIONAL_NOTES: 'ADDITIONAL_NOTES',
  ACCORDION_GROUP: 'ACCORDION_GROUP',
  EVALUATION_CRITERIA: 'EVALUATION_CRITERIA',
};

// Static tool configuration with i18n keys
export const TEACHER_TOOLS = [
  {
    id: 'question-generator',
    titleKey: 'tool_question_generator_title',
    descriptionKey: 'tool_question_generator_description',
    longDescriptionKey: 'tool_question_generator_long_description',
    icon: 'sparkles',
    iconColor: '#3B82F6', // Blue
    category: 'assessment',
    available: true,
    opensDialog: 'QuizLocationDialog',
  },
  {
    id: 'text-level-adapter',
    titleKey: 'tool_text_level_adapter_title',
    descriptionKey: 'tool_text_level_adapter_description',
    longDescriptionKey: 'tool_text_level_adapter_long_description',
    setupRoute: '/teacher-tools/setup/text-level-adapter',
    setupComponent: 'RealWorldConnectionSetup', // Will be replaced with specific component later
    icon: 'pencil-color',
    iconColor: '#8B5CF6', // Purple
    category: 'content',
    available: true,
    // Dynamic form configuration
    fields: [
      {
        id: 'education',
        type: FIELD_TYPES.EDUCATION_YEAR,
        required: true,
        showLabel: true,
        order: 1,
      },
      {
        id: 'textOrLesson',
        type: FIELD_TYPES.TEXT_OR_LESSON,
        required: true,
        labelKey: 'teacher_tools_adapter_text_label',
        placeholderKey: 'Write, paste text or choose from your books',
        showLabel: true,
        wordLimit: 10000,
        order: 2,
      },
      {
        id: 'specialNeeds',
        type: FIELD_TYPES.SPECIAL_NEEDS,
        required: false,
        labelKey: 'teacher_tools_special_needs_label',
        order: 3,
      },
      {
        id: 'additionalNotes',
        type: FIELD_TYPES.ADDITIONAL_NOTES,
        required: false,
        labelKey: 'teacher_tools_additional_notes_label',
        placeholderKey: 'teacher_tools_additional_notes_placeholder',
        rows: 3,
        order: 4,
      },
    ],
    transformPayload: (formData) => {
      return {
        educationYear: formData.education.educationYearLabel,
        educationLevel: formData.education.educationLevelLabel,
        textToAdapt: formData.textOrLesson.text,
        specialNeeds: formData.specialNeeds?.length > 0 ? formData.specialNeeds.join(', ') : undefined,
        additionalNotes: formData.additionalNotes || undefined,
      };
    },
    apiMethod: 'createTextLevelAdapter',
    canvasMessageKey: 'teacher_tools_text_level_adapter_canvas_message',
  },
  {
    id: 'lesson-plan-generator',
    titleKey: 'tool_lesson_plan_generator_title',
    descriptionKey: 'tool_lesson_plan_generator_description',
    longDescriptionKey: 'tool_lesson_plan_generator_long_description',
    setupRoute: '/teacher-tools/setup/lesson-plan-generator',
    setupComponent: 'GenericToolSetup', // Will be replaced with specific component later
    icon: 'magic-wand-color',
    iconColor: '#10B981', // Green
    category: 'planning',
    available: true,
    // Dynamic form configuration
    fields: [
      {
        id: 'education',
        type: FIELD_TYPES.EDUCATION_YEAR,
        required: true,
        showLabel: true,
        order: 1,
      },
      {
        id: 'textOrLesson',
        type: FIELD_TYPES.TEXT_OR_LESSON,
        required: true,
        labelKey: 'teacher_tools_topic_label',
        placeholderKey: 'Write, paste text or choose from your books',
        showLabel: true,
        wordLimit: 10000,
        order: 2,
      },
      {
        id: 'lessonDuration',
        type: FIELD_TYPES.NUMBER,
        required: true,
        labelKey: 'teacher_tools_lesson_duration_label',
        placeholderKey: 'teacher_tools_lesson_duration_placeholder',
        min: 15,
        max: 180,
        defaultValue: 45,
        order: 3,
      },
      {
        id: 'optionalDetails',
        type: FIELD_TYPES.ACCORDION_GROUP,
        labelKey: 'teacher_tools_optional_details_label',
        order: 4,
        fields: [
          {
            id: 'evaluationCriteria',
            type: FIELD_TYPES.EVALUATION_CRITERIA,
            required: false,
            order: 1,
          },
          {
            id: 'specialNeeds',
            type: FIELD_TYPES.SPECIAL_NEEDS,
            required: false,
            labelKey: 'teacher_tools_special_needs_label',
            order: 2,
          },
          {
            id: 'availableResources',
            type: FIELD_TYPES.TEXTAREA,
            required: false,
            labelKey: 'teacher_tools_available_resources_label',
            placeholderKey: 'teacher_tools_available_resources_placeholder',
            rows: 3,
            order: 3,
          },
          {
            id: 'additionalNotes',
            type: FIELD_TYPES.ADDITIONAL_NOTES,
            required: false,
            labelKey: 'teacher_tools_additional_notes_label',
            placeholderKey: 'teacher_tools_additional_notes_placeholder',
            rows: 3,
            order: 4,
          },
        ],
      },
    ],
    transformPayload: (formData) => {
      const payload = {
        educationYear: formData.education?.educationYearLabel,
        educationLevel: formData.education?.educationLevelLabel,
        textToAdapt: formData.textOrLesson?.text,
        duration: `${formData.lessonDuration || 45} minutos`,
      };

      // Build evaluationCriteria array from composite field
      const criteriaArray = [];

      // Add selected learning objectives
      if (formData.evaluationCriteria?.selected?.length > 0) {
        const selectedLabels = formData.evaluationCriteria.selected.map((lo) => lo.learning_objective);
        criteriaArray.push(...selectedLabels);
      }

      // Parse additional criteria (line-separated text)
      if (formData.evaluationCriteria?.additional) {
        const additionalParsed = formData.evaluationCriteria.additional
          .split(/\n/)
          .map((c) => c.trim())
          .filter((c) => c.length > 0);
        criteriaArray.push(...additionalParsed);
      }

      // Only add evaluationCriteria if we have at least one criterion
      if (criteriaArray.length > 0) {
        payload.evaluationCriteria = criteriaArray;
      }

      // Add other optional fields
      if (formData.specialNeeds?.length > 0) {
        payload.specialNeeds = formData.specialNeeds.join(', ');
      }
      if (formData.availableResources) {
        payload.resources = formData.availableResources;
      }
      if (formData.additionalNotes) {
        payload.additionalNotes = formData.additionalNotes;
      }

      return payload;
    },
    apiMethod: 'createLessonPlanGenerator',
    canvasMessageKey: 'teacher_tools_lesson_plan_generator_canvas_message',
  },
  {
    id: 'real-world-connection',
    titleKey: 'tool_real_world_connection_title',
    descriptionKey: 'tool_real_world_connection_description',
    longDescriptionKey: 'tool_real_world_connection_long_description',
    setupRoute: '/teacher-tools/setup/real-world-connection',
    setupComponent: 'RealWorldConnectionSetup',
    icon: 'globe-showing-europe-africa',
    iconColor: '#EC4899', // Pink
    category: 'engagement',
    available: true,
    // Dynamic form configuration
    fields: [
      {
        id: 'education',
        type: FIELD_TYPES.EDUCATION_YEAR,
        required: true,
        showLabel: true,
        order: 1,
      },
      {
        id: 'textOrLesson',
        type: FIELD_TYPES.TEXT_OR_LESSON,
        required: true,
        labelKey: 'teacher_tools_real_world_text_label',
        placeholderKey: 'Write, paste text or choose from your books',
        showLabel: true,
        wordLimit: 10000,
        order: 2,
      },
      {
        id: 'additionalNotes',
        type: FIELD_TYPES.ADDITIONAL_NOTES,
        required: false,
        labelKey: 'teacher_tools_additional_notes_label',
        placeholderKey: 'teacher_tools_additional_notes_placeholder',
        rows: 3,
        order: 3,
      },
    ],
    transformPayload: (formData) => {
      return {
        educationYear: formData.education.educationYearLabel,
        educationLevel: formData.education.educationLevelLabel,
        textToAdapt: formData.textOrLesson.text,
        additionalNotes: formData.additionalNotes || undefined,
      };
    },
    apiMethod: 'createRealWorldConnection',
    canvasMessageKey: 'teacher_tools_real_world_connection_canvas_message',
  },
  {
    id: 'rubric-generator',
    titleKey: 'tool_rubric_generator_title',
    descriptionKey: 'tool_rubric_generator_description',
    longDescriptionKey: 'tool_rubric_generator_long_description',
    setupRoute: '/teacher-tools/setup/rubric-generator',
    setupComponent: 'GenericToolSetup',
    icon: 'homework',
    iconColor: '#F59E0B', // Orange
    category: 'assessment',
    available: true,
    // Dynamic form configuration
    fields: [
      {
        id: 'education',
        type: FIELD_TYPES.EDUCATION_YEAR,
        required: true,
        showLabel: true,
        order: 1,
      },
      {
        id: 'textOrLesson',
        type: FIELD_TYPES.TEXT_OR_LESSON,
        required: true,
        labelKey: 'teacher_tools_rubric_text_label',
        placeholderKey: 'teacher_tools_rubric_text_placeholder',
        showLabel: true,
        wordLimit: 340000,
        order: 2,
      },
      {
        id: 'numberOfCriteria',
        type: FIELD_TYPES.NUMBER,
        required: true,
        labelKey: 'teacher_tools_rubric_criteria_count_label',
        placeholderKey: 'teacher_tools_rubric_criteria_count_placeholder',
        min: 1,
        max: 8,
        defaultValue: 4,
        order: 3,
      },
      {
        id: 'scale',
        type: FIELD_TYPES.DROPDOWN,
        required: true,
        labelKey: 'teacher_tools_rubric_scale_label',
        order: 4,
        isMulti: false,
        options: [
          { value: '1-4', labelKey: 'teacher_tools_rubric_scale_1_4' },
          { value: '1-6', labelKey: 'teacher_tools_rubric_scale_1_6' },
          { value: '1-10', labelKey: 'teacher_tools_rubric_scale_1_10' },
        ],
      },
      {
        id: 'optionalDetails',
        type: FIELD_TYPES.ACCORDION_GROUP,
        labelKey: 'teacher_tools_optional_details_label',
        order: 5,
        fields: [
          {
            id: 'evaluationCriteria',
            type: FIELD_TYPES.EVALUATION_CRITERIA,
            required: false,
            order: 1,
          },
          {
            id: 'specialNeeds',
            type: FIELD_TYPES.SPECIAL_NEEDS,
            required: false,
            labelKey: 'teacher_tools_special_needs_label',
            order: 2,
          },
          {
            id: 'additionalNotes',
            type: FIELD_TYPES.ADDITIONAL_NOTES,
            required: false,
            labelKey: 'teacher_tools_additional_notes_label',
            placeholderKey: 'teacher_tools_additional_notes_placeholder',
            rows: 3,
            order: 3,
          },
        ],
      },
    ],
    transformPayload: (formData) => {
      const payload = {
        educationYear: formData.education?.educationYearLabel,
        educationLevel: formData.education?.educationLevelLabel,
        textToAdapt: formData.textOrLesson?.text,
        numberOfCriteria: formData.numberOfCriteria || 4,
        scale: formData.scale,
      };

      // Build evaluationCriteria array from composite field
      const criteriaArray = [];

      // Add selected learning objectives
      if (formData.evaluationCriteria?.selected?.length > 0) {
        const selectedLabels = formData.evaluationCriteria.selected.map((lo) => lo.learning_objective);
        criteriaArray.push(...selectedLabels);
      }

      // Parse additional criteria (line-separated text)
      if (formData.evaluationCriteria?.additional) {
        const additionalParsed = formData.evaluationCriteria.additional
          .split(/\n/)
          .map((c) => c.trim())
          .filter((c) => c.length > 0);
        criteriaArray.push(...additionalParsed);
      }

      // Only add evaluationCriteria if we have at least one criterion
      if (criteriaArray.length > 0) {
        payload.evaluationCriteria = criteriaArray;
      }

      // Add other optional fields
      if (formData.specialNeeds?.length > 0) {
        payload.specialNeeds = formData.specialNeeds.join(', ');
      }
      if (formData.additionalNotes) {
        payload.additionalNotes = formData.additionalNotes;
      }

      return payload;
    },
    apiMethod: 'createRubricGenerator',
    canvasMessageKey: 'teacher_tools_rubric_generator_canvas_message',
  },
  {
    id: 'vocabulary-creator',
    titleKey: 'tool_vocabulary_creator_title',
    descriptionKey: 'tool_vocabulary_creator_description',
    longDescriptionKey: 'tool_vocabulary_creator_long_description',
    setupRoute: '/teacher-tools/setup/vocabulary-creator',
    setupComponent: 'RealWorldConnectionSetup', // Will be replaced with specific component later
    icon: 'books',
    iconColor: '#14B8A6', // Teal
    category: 'content',
    available: true,
    // Dynamic form configuration
    fields: [
      {
        id: 'education',
        type: FIELD_TYPES.EDUCATION_YEAR,
        required: true,
        showLabel: true,
        order: 1,
      },
      {
        id: 'textOrLesson',
        type: FIELD_TYPES.TEXT_OR_LESSON,
        required: true,
        labelKey: 'teacher_tools_vocabulary_text_label',
        placeholderKey: 'Write, paste text or choose from your books',
        showLabel: true,
        wordLimit: 10000,
        order: 2,
      },
      {
        id: 'numberOfWordsToExtract',
        type: FIELD_TYPES.NUMBER,
        required: true,
        labelKey: 'teacher_tools_vocabulary_word_count_label',
        placeholderKey: 'teacher_tools_vocabulary_word_count_placeholder',
        min: 1,
        max: 50,
        defaultValue: 10,
        order: 3,
      },
      {
        id: 'specialNeeds',
        type: FIELD_TYPES.SPECIAL_NEEDS,
        required: false,
        labelKey: 'teacher_tools_special_needs_label',
        order: 4,
      },
      {
        id: 'additionalNotes',
        type: FIELD_TYPES.ADDITIONAL_NOTES,
        required: false,
        labelKey: 'teacher_tools_additional_notes_label',
        placeholderKey: 'teacher_tools_additional_notes_placeholder',
        rows: 3,
        order: 5,
      },
    ],
    transformPayload: (formData) => {
      return {
        educationYear: formData.education.educationYearLabel,
        educationLevel: formData.education.educationLevelLabel,
        textToAdapt: formData.textOrLesson.text,
        numberOfWordsToExtract: formData.numberOfWordsToExtract || 10,
        specialNeeds: formData.specialNeeds?.length > 0 ? formData.specialNeeds.join(', ') : undefined,
        additionalNotes: formData.additionalNotes || undefined,
      };
    },
    apiMethod: 'createVocabularyCreator',
    canvasMessageKey: 'teacher_tools_vocabulary_creator_canvas_message',
  },
  {
    id: 'text-translator',
    titleKey: 'tool_text_translator_title',
    descriptionKey: 'tool_text_translator_description',
    longDescriptionKey: 'tool_text_translator_long_description',
    setupRoute: '/teacher-tools/setup/text-translator',
    setupComponent: 'RealWorldConnectionSetup',
    icon: 'globe-with-meridians',
    iconColor: '#6366F1', // Indigo
    category: 'content',
    available: true,
    // Dynamic form configuration
    fields: [
      {
        id: 'education',
        type: FIELD_TYPES.EDUCATION_YEAR,
        required: true,
        showLabel: true,
        order: 1,
      },
      {
        id: 'textOrLesson',
        type: FIELD_TYPES.TEXT_OR_LESSON,
        required: true,
        labelKey: 'teacher_tools_text_to_translate_label',
        placeholderKey: 'Write, paste text or choose from your books',
        showLabel: true,
        wordLimit: 10000,
        order: 2,
      },
      {
        id: 'destinationLanguage',
        type: FIELD_TYPES.DROPDOWN,
        required: true,
        labelKey: 'teacher_tools_destination_language_label',
        order: 3,
        isMulti: false,
        sortOptions: true,
        options: [
          { value: 'es', labelKey: 'language_es' },
          { value: 'ca-valencia', labelKey: 'language_ca_valencia' },
          { value: 'ca', labelKey: 'language_ca' },
          { value: 'eu', labelKey: 'language_eu' },
          { value: 'gl', labelKey: 'language_gl' },
          { value: 'es-CO', labelKey: 'language_es_co' },
          { value: 'en', labelKey: 'language_en' },
          { value: 'fr', labelKey: 'language_fr' },
          { value: 'it', labelKey: 'language_it' },
          { value: 'zh', labelKey: 'language_zh' },
          { value: 'ar', labelKey: 'language_ar' },
          { value: 'pt-BR', labelKey: 'language_pt_br' },
        ],
      },
      {
        id: 'specialNeeds',
        type: FIELD_TYPES.SPECIAL_NEEDS,
        required: false,
        labelKey: 'teacher_tools_special_needs_label',
        order: 4,
      },
      {
        id: 'additionalNotes',
        type: FIELD_TYPES.ADDITIONAL_NOTES,
        required: false,
        labelKey: 'teacher_tools_additional_notes_label',
        placeholderKey: 'teacher_tools_additional_notes_placeholder',
        rows: 3,
        order: 5,
      },
    ],
    transformPayload: (formData) => {
      return {
        educationYear: formData.education.educationYearLabel,
        educationLevel: formData.education.educationLevelLabel,
        textToAdapt: formData.textOrLesson.text,
        destinationLanguage: formData.destinationLanguage,
        specialNeeds: formData.specialNeeds?.length > 0 ? formData.specialNeeds.join(', ') : undefined,
        additionalNotes: formData.additionalNotes || undefined,
      };
    },
    apiMethod: 'createTextTranslator',
    canvasMessageKey: 'teacher_tools_text_translator_canvas_message',
  },
  {
    id: 'classroom-energizer',
    titleKey: 'tool_classroom_energizer_title',
    descriptionKey: 'tool_classroom_energizer_description',
    longDescriptionKey: 'tool_classroom_energizer_long_description',
    setupRoute: '/teacher-tools/setup/classroom-energizer',
    setupComponent: 'RealWorldConnectionSetup', // Will be replaced with specific component later
    icon: 'party-popper',
    iconColor: '#EF4444', // Red
    category: 'engagement',
    available: true,
    fields: [
      {
        id: 'education',
        type: FIELD_TYPES.EDUCATION_YEAR,
        required: true,
        showLabel: true,
        order: 1,
      },
      {
        id: 'textOrLesson',
        type: FIELD_TYPES.TEXT_OR_LESSON,
        required: true,
        labelKey: 'teacher_tools_energizer_text_label',
        placeholderKey: 'Write, paste text or choose from your books',
        showLabel: true,
        wordLimit: 10000,
        order: 2,
      },
      {
        id: 'additionalNotes',
        type: FIELD_TYPES.ADDITIONAL_NOTES,
        required: false,
        labelKey: 'teacher_tools_additional_notes_label',
        placeholderKey: 'teacher_tools_additional_notes_placeholder',
        rows: 3,
        order: 3,
      },
    ],
    transformPayload: (formData) => {
      return {
        educationYear: formData.education.educationYearLabel,
        educationLevel: formData.education.educationLevelLabel,
        textToAdapt: formData.textOrLesson.text,
        additionalNotes: formData.additionalNotes || undefined,
      };
    },
    apiMethod: 'createClassroomDynamics',
    canvasMessageKey: 'teacher_tools_classroom_energizer_canvas_message',
  },
  {
    id: 'student-feedback',
    titleKey: 'tool_student_feedback_title',
    descriptionKey: 'tool_student_feedback_description',
    longDescriptionKey: 'tool_student_feedback_long_description',
    setupRoute: '/teacher-tools/setup/student-feedback',
    setupComponent: 'GenericToolSetup',
    icon: 'memo',
    iconColor: '#06B6D4', // Cyan
    category: 'communication',
    available: true,
    // Dynamic form configuration
    fields: [
      {
        id: 'education',
        type: FIELD_TYPES.EDUCATION_YEAR,
        required: true,
        showLabel: true,
        order: 1,
      },
      {
        id: 'studentWork',
        type: FIELD_TYPES.TEXTAREA_WITH_COUNT,
        required: true,
        labelKey: 'teacher_tools_student_work_label',
        placeholderKey: 'teacher_tools_student_work_placeholder',
        wordLimit: 10000,
        rows: 3,
        order: 2,
      },
      {
        id: 'expectedResponse',
        type: FIELD_TYPES.TEXTAREA_WITH_COUNT,
        required: false,
        labelKey: 'teacher_tools_expected_response_label',
        placeholderKey: 'teacher_tools_expected_response_placeholder',
        wordLimit: 10000,
        rows: 3,
        order: 3,
      },
      {
        id: 'optionalDetails',
        type: FIELD_TYPES.ACCORDION_GROUP,
        labelKey: 'teacher_tools_optional_details_label',
        order: 4,
        fields: [
          {
            id: 'evaluationCriteria',
            type: FIELD_TYPES.EVALUATION_CRITERIA,
            required: false,
            order: 1,
          },
          {
            id: 'specialNeeds',
            type: FIELD_TYPES.SPECIAL_NEEDS,
            required: false,
            labelKey: 'teacher_tools_special_needs_label',
            order: 2,
          },
          {
            id: 'additionalNotes',
            type: FIELD_TYPES.ADDITIONAL_NOTES,
            required: false,
            labelKey: 'teacher_tools_additional_notes_label',
            placeholderKey: 'teacher_tools_additional_notes_placeholder',
            rows: 3,
            order: 3,
          },
        ],
      },
    ],
    transformPayload: (formData) => {
      const payload = {
        educationYear: formData.education?.educationYearLabel,
        educationLevel: formData.education?.educationLevelLabel,
        textToAdapt: formData.studentWork,
      };

      // Expected response (optional)
      if (formData.expectedResponse) {
        payload.expectedResponse = formData.expectedResponse;
      }

      // Build evaluationCriteria array from composite field
      const criteriaArray = [];

      // Add selected learning objectives
      if (formData.evaluationCriteria?.selected?.length > 0) {
        const selectedLabels = formData.evaluationCriteria.selected.map((lo) => lo.learning_objective);
        criteriaArray.push(...selectedLabels);
      }

      // Parse additional criteria (line-separated text)
      if (formData.evaluationCriteria?.additional) {
        const additionalParsed = formData.evaluationCriteria.additional
          .split(/\n/)
          .map((c) => c.trim())
          .filter((c) => c.length > 0);
        criteriaArray.push(...additionalParsed);
      }

      // Only add evaluationCriteria if we have at least one criterion
      if (criteriaArray.length > 0) {
        payload.evaluationCriteria = criteriaArray;
      }

      // Add other optional fields
      if (formData.specialNeeds?.length > 0) {
        payload.specialNeeds = formData.specialNeeds.join(', ');
      }
      if (formData.additionalNotes) {
        payload.additionalNotes = formData.additionalNotes;
      }

      return payload;
    },
    apiMethod: 'createStudentFeedback',
    canvasMessageKey: 'teacher_tools_student_feedback_canvas_message',
  },
];

// Generate route constants from tool configuration
export const TOOL_ROUTES = TEACHER_TOOLS.reduce((acc, tool) => {
  const key = `TEACHER_TOOL_${tool.id.toUpperCase().replace(/-/g, '_')}`;
  acc[key] = tool.setupRoute;
  return acc;
}, {});

// Backward compatibility - function that returns tools with translated labels
export const getTeacherTools = (t) =>
  TEACHER_TOOLS.map((tool) => ({
    ...tool,
    title: t(tool.titleKey),
    description: t(tool.descriptionKey),
  }));

// Explicit mapping for API mode values to tool IDs
export const TOOL_MODE_MAP = {
  TEACHER_TOOLS_REAL_WORLD_CONNECTION: 'real-world-connection',
  TEACHER_TOOLS_TEXT_LEVEL_ADAPTATION: 'text-level-adapter',
  TEACHER_TOOLS_LESSON_PLAN_GENERATOR: 'lesson-plan-generator',
  TEACHER_TOOLS_RUBRIC_GENERATOR: 'rubric-generator',
  TEACHER_TOOLS_VOCABULARY_CREATOR: 'vocabulary-creator',
  TEACHER_TOOLS_TEXT_TRANSLATOR: 'text-translator',
  TEACHER_TOOLS_STUDENT_FEEDBACK: 'student-feedback',
  TEACHER_TOOLS_QUESTION_GENERATOR: 'question-generator',
  TEACHER_TOOLS_CLASSROOM_DYNAMICS: 'classroom-energizer',
};

// Helper function to parse API mode string to tool ID
export const parseToolMode = (mode) => {
  if (!mode) return null;
  // Use explicit mapping for certainty
  return TOOL_MODE_MAP[mode] || null;
};

// Helper function to get tool configuration by ID (no translation needed)
export const getToolById = (toolId) => {
  if (!toolId) return null;
  return TEACHER_TOOLS.find((tool) => tool.id === toolId);
};

/**
 * Get tool field configuration by tool ID (reads from TEACHER_TOOLS array)
 * @param {string} toolId - Tool identifier (e.g., 'vocabulary-creator')
 * @returns {Object|null} Tool field configuration or null if not found
 */
export const getToolFieldsConfig = (toolId) => {
  const tool = getToolById(toolId);
  if (!tool || !tool.fields) return null;

  return {
    fields: tool.fields,
    transformPayload: tool.transformPayload,
    apiMethod: tool.apiMethod,
    canvasMessageKey: tool.canvasMessageKey,
  };
};

/**
 * Get sorted fields for a tool
 * @param {string} toolId - Tool identifier
 * @returns {Array} Sorted array of field definitions
 */
export const getToolFields = (toolId) => {
  const tool = getToolById(toolId);
  if (!tool || !tool.fields) return [];

  return [...tool.fields].sort((a, b) => a.order - b.order);
};
